import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText, Download, Loader2, TrendingUp, CreditCard, ArrowUpRight, ArrowDownRight, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';

const MONTH_NAMES = {
  fr: ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
  en: ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
};

const T = {
  fr: {
    title: 'Rapports',
    subtitle: 'Consultez et téléchargez vos rapports mensuels',
    noReports: 'Aucun rapport disponible pour le moment.',
    noReportsDesc: 'Les rapports sont générés automatiquement à partir de vos transactions.',
    revenue: 'Chiffre d\'affaires',
    transactions: 'Transactions',
    avgBasket: 'Panier moyen',
    fees: 'Frais totaux',
    netRevenue: 'Revenu net',
    refunds: 'Remboursements',
    download: 'Télécharger PDF',
    downloading: 'Génération...',
    selectMonth: 'Sélectionnez un mois pour voir le résumé',
  },
  en: {
    title: 'Reports',
    subtitle: 'View and download your monthly reports',
    noReports: 'No reports available yet.',
    noReportsDesc: 'Reports are automatically generated from your transactions.',
    revenue: 'Revenue',
    transactions: 'Transactions',
    avgBasket: 'Avg Basket',
    fees: 'Total Fees',
    netRevenue: 'Net Revenue',
    refunds: 'Refunds',
    download: 'Download PDF',
    downloading: 'Generating...',
    selectMonth: 'Select a month to view summary',
  },
};

export default function Reports() {
  const { lang } = useLanguage();
  const t = T[lang] || T.en;
  const monthNames = MONTH_NAMES[lang] || MONTH_NAMES.en;

  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const fetchMonths = async () => {
      try {
        const res = await api.get('/reports/available');
        setMonths(res.data.months || []);
        if (res.data.months?.length > 0) {
          setSelectedMonth(res.data.months[0]);
        }
      } catch {
        // owner/admin only
      } finally {
        setLoading(false);
      }
    };
    fetchMonths();
  }, []);

  useEffect(() => {
    if (!selectedMonth) return;
    const fetchSummary = async () => {
      setSummaryLoading(true);
      try {
        const res = await api.get(`/reports/summary/${selectedMonth.year}/${selectedMonth.month}`);
        setSummary(res.data);
      } catch {
        setSummary(null);
      } finally {
        setSummaryLoading(false);
      }
    };
    fetchSummary();
  }, [selectedMonth]);

  const handleDownload = async () => {
    if (!selectedMonth) return;
    setDownloading(true);
    try {
      const res = await api.get(`/reports/download/${selectedMonth.year}/${selectedMonth.month}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `EmergencyPOS_Rapport_${selectedMonth.year}-${String(selectedMonth.month).padStart(2, '0')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(lang === 'fr' ? 'Rapport téléchargé' : 'Report downloaded');
    } catch {
      toast.error(lang === 'fr' ? 'Erreur lors du téléchargement' : 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const fmt = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="reports-loading">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t.title}</h1>
        <p className="text-sm text-muted-foreground">{t.subtitle}</p>
      </div>

      {months.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="font-medium text-foreground">{t.noReports}</p>
            <p className="text-sm text-muted-foreground mt-1">{t.noReportsDesc}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          {/* Month selector */}
          <Card>
            <CardContent className="p-3">
              <div className="space-y-1" data-testid="reports-month-list">
                {months.map((m) => {
                  const isActive = selectedMonth?.label === m.label;
                  return (
                    <button
                      key={m.label}
                      onClick={() => setSelectedMonth(m)}
                      data-testid={`report-month-${m.label}`}
                      className={`w-full text-left px-3 py-2.5 text-sm font-medium transition-colors flex items-center gap-2 ${
                        isActive
                          ? 'bg-orange-500/10 text-orange-500 border-l-2 border-orange-500'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                    >
                      <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                      {monthNames[m.month]} {m.year}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Summary */}
          <div className="space-y-4">
            {summaryLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-orange-500" />
              </div>
            ) : summary ? (
              <>
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold" data-testid="report-summary-title">
                    {monthNames[summary.month]} {summary.year}
                  </h2>
                  <Button
                    onClick={handleDownload}
                    disabled={downloading}
                    className="bg-orange-500 hover:bg-orange-600 text-white font-semibold"
                    data-testid="report-download-btn"
                  >
                    {downloading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t.downloading}</>
                    ) : (
                      <><Download className="w-4 h-4 mr-2" /> {t.download}</>
                    )}
                  </Button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { label: t.revenue, value: fmt(summary.revenue), icon: TrendingUp, color: 'text-emerald-500' },
                    { label: t.transactions, value: summary.paid_count, icon: CreditCard, color: 'text-blue-500' },
                    { label: t.avgBasket, value: fmt(summary.avg_basket), icon: ArrowUpRight, color: 'text-purple-500' },
                    { label: t.fees, value: fmt(summary.total_fees), icon: ArrowDownRight, color: 'text-red-400' },
                    { label: t.netRevenue, value: fmt(summary.net_revenue), icon: TrendingUp, color: 'text-emerald-500' },
                    { label: t.refunds, value: summary.refund_count, icon: ArrowDownRight, color: 'text-orange-400' },
                  ].map((s) => (
                    <Card key={s.label}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-1.5 mb-1">
                          <s.icon className={`w-3.5 h-3.5 ${s.color}`} />
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{s.label}</span>
                        </div>
                        <p className="text-xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{s.value}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Fee detail card */}
                <Card>
                  <CardContent className="p-4">
                    <h3 className="text-sm font-bold mb-3">{lang === 'fr' ? 'Détail des frais' : 'Fee Breakdown'}</h3>
                    <div className="space-y-2">
                      {[
                        { label: lang === 'fr' ? 'Frais plateforme' : 'Platform fees', value: fmt(summary.platform_fees) },
                        { label: lang === 'fr' ? 'Frais Stripe (estimés)' : 'Stripe fees (est.)', value: fmt(summary.stripe_fees) },
                      ].map((row) => (
                        <div key={row.label} className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">{row.label}</span>
                          <span className="font-medium font-mono">{row.value}</span>
                        </div>
                      ))}
                      <div className="border-t pt-2 flex items-center justify-between text-sm font-bold">
                        <span>{lang === 'fr' ? 'Total des frais' : 'Total fees'}</span>
                        <span className="font-mono text-red-400">{fmt(summary.total_fees)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm font-bold">
                        <span>{lang === 'fr' ? 'Revenu net estimé' : 'Est. net revenue'}</span>
                        <span className="font-mono text-emerald-500">{fmt(summary.net_revenue)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-sm text-muted-foreground">{t.selectMonth}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
